<script setup lang="ts">
import { ButtonComp, ContentComp, InputCheckBox, InputNullableInt, InputRadio, ModalComp } from '@/components';
import { CategoriesFilter } from '@/components';
import { FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import FormLayout from '@/components/Form/FormLayout.vue';
import { useDialog, useLoaders, useTaskLoader } from '@/composeables';
import { BibAssignDao } from '@/dao';
import { SetSeedingGroupRequest, type SetSeedingGroupType } from '@/objects/generated';
import { useEventStoreRef, useToastStore } from '@/stores';
import { ref } from 'vue';

const { AddLoader, LoadState } = useTaskLoader();

const { eventID } = useEventStoreRef();

const modal = ref<InstanceType<typeof ModalComp> | null>(null);

const emit = defineEmits<{
    (e: 'closed'): void;
}>();

defineExpose({
    show: () => {
        modal.value?.show();
    },
});

const close = () => {
    modal.value?.hide();
    emit('closed');
};

const assign = async () => {
    // check that we have only one category selected when we are running nmabcd
    if (['NmABCD', 'NmABC'].includes(SeedingGroupType.value) && categoryids.value.length !== 1) {
        useDialog().Ok({ title: 'bibassign.only_one_category', body: 'bibassign.only_one_category' });
        return;
    }
    // send request
    const req = SetSeedingGroupRequest.FromJsValues({
        categoryIDs: categoryids.value,
        ResetAllCategories: resetAllCategories.value,
        SetSeedingGroupType: SeedingGroupType.value,
        SetGroupSize: setgroupsize.value,
        NmABCD: nmabcd.value,
        NmABC: nmabc.value,
    });
    const resp = await BibAssignDao.BibAssignSetSeedingGroups(eventID.value, req);
    // check response
    if (resp?.Success) {
        useToastStore().addSuccess(resp?.TranslationKey ?? 'bibassign.seeding_group_assigned');
        close();
    } else {
        useToastStore().addError(resp?.TranslationKey ?? 'bibassign.seeding_group_not_assigned');
    }
};

const categoryids = ref<number[]>([]);
const resetAllCategories = ref<boolean>(false);
const SeedingGroupType = ref<SetSeedingGroupType>('Random');
const setgroupsize = ref<number | null>(null);
const nmabcd = ref<number | null>(null);
const nmabc = ref<number | null>(null);

useLoaders(AddLoader, ['CONTESTANTS', 'CATEGORIES']);
</script>
<template>
    <ModalComp
        ref="modal"
        @closed="() => emit('closed')"
        :title="$tm('bibassign.seedinggroups')"
        :title-icon="'fa-list-ol'"
        size="md"
    >
        <template #content>
            <ContentComp :loadState="LoadState">
                <FormLayout :label-width="50">
                    <FormLayoutRow v-if="categoryids.length > 4">
                        <FormLayoutLabel :text="'bibassign.choose_categories'" />
                        <CategoriesFilter
                            v-model="categoryids"
                            :selectAllOptionsOnLoad="true"
                        />
                    </FormLayoutRow>
                    <template v-else>
                        <FormLayoutRow>
                            <FormLayoutLabel :text="'bibassign.choose_categories'" />
                            <!-- TODO: Layout checbox for each category, no need for list when we have this few -->
                            <CategoriesFilter
                                v-model="categoryids"
                                :selectAllOptionsOnLoad="true"
                            />
                        </FormLayoutRow>
                    </template>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <InputCheckBox
                            v-model="resetAllCategories"
                            id="resetAllCategories"
                            :translationkey="{
                                key: 'bibassign.reset_seeding_group_on_all_categories',
                                default: 'Reset seeding group on all categories',
                            }"
                        />
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <InputRadio
                            value="ScandinavianCup"
                            v-model="SeedingGroupType"
                            id="scandinavianCup"
                            translationkey="bibassign.scandinavian_cup"
                        />
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <InputRadio
                            value="Random"
                            v-model="SeedingGroupType"
                            id="Random"
                            translationkey="bibassign.random"
                        />
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <InputRadio
                                value="SetGroupSize"
                                v-model="SeedingGroupType"
                                id="SetGroupSize"
                                translationkey="bibassign.set_group_size"
                            />
                            <div class="w-50">
                                <InputNullableInt
                                    v-model="setgroupsize"
                                    :selectAllOptionsOnLoad="true"
                                />
                            </div>
                        </div>
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <InputRadio
                                value="NmABCD"
                                v-model="SeedingGroupType"
                                id="NMABCD"
                                translationkey="bibassign.nmabcd"
                            />
                            <div class="w-50">
                                <InputNullableInt
                                    v-model="nmabcd"
                                    :selectAllOptionsOnLoad="true"
                                />
                            </div>
                        </div>
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel />
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <InputRadio
                                value="NmABC"
                                v-model="SeedingGroupType"
                                id="NMABC"
                                translationkey="bibassign.nmabc"
                            />
                            <div class="w-50">
                                <InputNullableInt
                                    v-model="nmabc"
                                    :selectAllOptionsOnLoad="true"
                                />
                            </div>
                        </div>
                    </FormLayoutRow>
                    <div class="d-flex justify-content-between mt-3">
                        <ButtonComp
                            @click="close"
                            :button-type="'Close'"
                            >{{ $tm('general.cancel') }}</ButtonComp
                        >
                        <ButtonComp
                            :icon="'fa-list-ol'"
                            @click="assign"
                            >{{ $tm('bibassign.assign') }}</ButtonComp
                        >
                    </div>
                </FormLayout>
            </ContentComp>
        </template>
    </ModalComp>
</template>
